import { Loading } from '@perch-labs/shrike';
import { useContext, useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { DevToolsButton } from '~/app/dev-tools';
import { Header } from '~/components/header';
import { Page } from '~/components/page';
import { Separator } from '~/components/separator';
import { SponsoredSolarRoot } from '~/funnels/sponsored-solar/sponsored-solar-root';
import { WelcomeBackWrapper } from '~/funnels/welcome-back/welcome-back.wrapper';
import { QueryParamsContext } from '~/providers/query-params';
import { RouterContext } from '~/providers/routing';
import { CreateAccountWrapper } from '~/screens/create-account/create-account.wrapper';
import { CreateAccountGoogleWrapper } from '~/screens/create-account-google/create-account-google.wrapper';
import { CredentialsWrapper } from '~/screens/credentials/credentials.wrapper';
import { LMIProofDocsUploadWrapper } from '~/screens/lmi-proof-docs-upload/lmi-proof-docs-upload.wrapper';
import { ReviewAndViewTermsWrapper } from '~/screens/review-and-view-terms/review-and-view-terms.wrapper';
import { ReviewFormWrapper } from '~/screens/review-form/review-form.wrapper';
import { SelectUtilityWrapper } from '~/screens/select-utility/select-utility.wrapper';
import { SignupWrapper } from '~/screens/signup/signup.wrapper';
import { updateOriginationSource } from '~/services';
import { OriginationSource } from '~/typings/graphql';
import { IS_PRODUCTION, SPONSORED_SOLAR_ROUTES } from '~/utils/constants';
import { DocusignSignedStatus, store } from '~/utils/store';

export const SponsoredSolar = () => {
  const navigate = useNavigate();
  const { docusignUser } = useContext(QueryParamsContext);
  const userId = store.get('USER_ID');
  const { nextRoute, loading } = useContext(RouterContext);
  const handleLMIRedirects = () => {
    // Make self attestation mandatory only if the user is non lmi and skipped proof doc
    if (!store.get('IS_IN_LMI_ZONE') && store.get('PROOF_DOC_SKIPPED')) {
      navigate(SPONSORED_SOLAR_ROUTES.REVIEW_LMI_FORM);
    } else {
      navigate(SPONSORED_SOLAR_ROUTES.REVIEW_AND_VIEW_TERMS);
    }
  };

  useEffect(() => {
    const docusignSignedStatus: DocusignSignedStatus[] = [
      'signing_complete',
      'cancel',
      'decline',
      'viewing_complete',
    ];
    if (docusignUser && docusignSignedStatus.includes(docusignUser?.signedStatus)) {
      store.set('DOCUSIGN_USER_SIGNED_STATUS', docusignUser.signedStatus);
      updateOriginationSource(store.get('USER_ID'), undefined, undefined, {
        docusignSignedStatus: docusignUser.signedStatus,
      });
    }
  }, []);

  useEffect(() => {
    navigate(nextRoute, { replace: true });
  }, [nextRoute]);
  return (
    <>
      {!IS_PRODUCTION && <DevToolsButton funnelId="sponsoredSolar" next={() => {}} />}
      <Header badge="Community solar" originationSource={OriginationSource.SponsoredSolar} />
      <Separator padding="0 0 40px 0" />
      <Page>
        {!loading ? (
          <Routes>
            <Route
              index
              path={SPONSORED_SOLAR_ROUTES.WELCOME_BACK}
              element={<WelcomeBackWrapper />}
            />
            <Route
              index
              path={SPONSORED_SOLAR_ROUTES.UTILITY_SELECTION}
              element={
                <SelectUtilityWrapper
                  onUtilitySelect={() => {
                    // navigate to user creation screen here.
                    navigate(SPONSORED_SOLAR_ROUTES.SIGNUP);
                  }}
                />
              }
            />
            <Route index path={SPONSORED_SOLAR_ROUTES.SIGNUP} element={<SignupWrapper />} />
            <Route
              index
              path={SPONSORED_SOLAR_ROUTES.USER_CREATION}
              element={
                <CreateAccountWrapper
                  originationSource={OriginationSource.SponsoredSolar}
                  onUserCreation={() => {
                    navigate(SPONSORED_SOLAR_ROUTES.UTILITY_CONNECT);
                  }}
                />
              }
            />
            <Route
              index
              path={SPONSORED_SOLAR_ROUTES.GOOGLE_USER}
              element={
                <CreateAccountGoogleWrapper
                  onUserCreation={() => {
                    // navigate to user creation screen here.
                    navigate(SPONSORED_SOLAR_ROUTES.UTILITY_CONNECT);
                  }}
                />
              }
            />
            {!!userId && (
              <Route
                index
                path={SPONSORED_SOLAR_ROUTES.UTILITY_CONNECT}
                element={
                  <CredentialsWrapper
                    onUtilityConnect={() => {
                      navigate(SPONSORED_SOLAR_ROUTES.PROOF_DOCS_UPLOAD);
                    }}
                  />
                }
              />
            )}
            {!!userId && (
              <Route
                index
                path={SPONSORED_SOLAR_ROUTES.PROOF_DOCS_UPLOAD}
                element={<LMIProofDocsUploadWrapper onProofDocUpload={handleLMIRedirects} />}
              />
            )}
            {!!userId && (
              <Route
                index
                path={SPONSORED_SOLAR_ROUTES.REVIEW_LMI_FORM}
                element={<ReviewFormWrapper />}
              />
            )}
            {!!userId && (
              <Route
                index
                path={SPONSORED_SOLAR_ROUTES.REVIEW_AND_VIEW_TERMS}
                element={<ReviewAndViewTermsWrapper />}
              />
            )}
            <Route path="*" element={<SponsoredSolarRoot />} />
          </Routes>
        ) : (
          <Loading />
        )}
      </Page>
    </>
  );
};
